import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { Constants, getValidSelectValue, getZeroOption, type Option } from '@/src/utils';
import {
    type InitialOptions,
    type ClassOption,
    type SchoolClassOptions,
    type ExamOption,
    type FilterState,
} from '../models/filterDTOs';
import {
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    SimpleButton,
    SimpleSingleSelect,
    FilterBarContainer,
} from '@/src/ui';

interface AttemptDetailsFiltersProps {
    refreshTableData: (filters: FilterState) => void;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
}

// --- Options ---
const LoadingOption = getZeroOption('Loading...');
const SelectDistrictOption = getZeroOption('Select District');
const SelectSchoolOption = getZeroOption('Select School');
const SelectClassOption = getZeroOption('Select Class');
const SelectGradeOption = getZeroOption('Select Grade');
const SelectExamOption = getZeroOption('Select Exam');
const AllStudentOption = getZeroOption('All');
const NoClassOption = getZeroOption('No Classes Available');
const NoExamOption = getZeroOption('No Exams Available');

const requiredFields = [
    'districtId',
    'schoolId',
    'gradeLevelMasterId',
    'classId',
    'examId',
] as const;

function getMissingFields(fs: any): string[] {
    return requiredFields
        .filter(
            (key) =>
                !fs[key] ||
                fs[key] === Number(SelectSchoolOption.id) ||
                fs[key] === Number(SelectGradeOption.id) ||
                fs[key] === Number(SelectClassOption.id) ||
                fs[key] === Number(SelectExamOption.id)
        )
        .map((key) => Constants.fieldLabels[key]);
}

const AttemptDetailsFilters: React.FC<AttemptDetailsFiltersProps> = ({
    refreshTableData,
    filterState,
    setFilterState,
}) => {
    const [error, setError] = useState('');
    const [fieldsChanged, setFieldsChanged] = useState(false);

    const [districtOptions, setDistrictOptions] = useState<Option[]>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([SelectSchoolOption]);
    const [classOptions, setClassOptions] = useState<Option[]>([SelectClassOption]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [examOptions, setExamOptions] = useState<Option[]>([SelectExamOption]);
    const [studentOptions, setStudentOptions] = useState<Option[]>([AllStudentOption]);

    const [classData, setClassData] = useState<ClassOption[]>([]);
    const [allExamOptions, setAllExamOptions] = useState<ExamOption[]>([]);
    const [examLoading, setExamLoading] = useState(false);

    const [filtersLoading, setFiltersLoading] = useState(true);

    const getLoadingProps = (opts: Option[], val: number, loading: boolean) =>
        loading
            ? { options: [LoadingOption], value: 0, disabled: true }
            : { options: opts, value: val };

    // --- Fetch school & class data ---
    const fetchSchoolClassOptions = async (districtId: number | string) => {
        if (!districtId || Number(districtId) === Number(SelectDistrictOption.id)) {
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({
                ...prev,
                schoolId: Number(SelectSchoolOption.id),
                gradeLevelMasterId: Number(SelectGradeOption.id),
                classId: Number(SelectClassOption.id),
            }));
            return;
        }

        try {
            const res = await apiRequest<SchoolClassOptions>(
                'GET',
                `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
            );
            const data = res.data;
            const newSchoolOptions = data.schoolOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setSchoolOptions([SelectSchoolOption, ...newSchoolOptions]);
            setFilterState((prev) => {
                const schoolExists = newSchoolOptions.some(
                    (opt) => Number(opt.id) === Number(prev.schoolId)
                );
                return {
                    ...prev,
                    schoolId: schoolExists ? prev.schoolId : Number(SelectSchoolOption.id),
                };
            });
            const sortedClassOptions = data.classOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            );
            setClassData(sortedClassOptions);
            setClassOptions([
                SelectClassOption,
                ...sortedClassOptions.map((option) => ({ id: option.id, name: option.name })),
            ]);
            setFilterState((prev) => {
                const classExists = sortedClassOptions.some(
                    (opt) => Number(opt.id) === Number(prev.classId)
                );
                return {
                    ...prev,
                    classId: classExists ? prev.classId : Number(SelectClassOption.id),
                };
            });
        } catch {
            setError('Failed to fetch school and class options');
            setSchoolOptions([SelectSchoolOption]);
            setClassOptions([SelectClassOption]);
        }
    };

    const updateClassOptions = (
        data: ClassOption[],
        gradeLevelMasterId: number,
        schoolId: number
    ) => {
        if (
            !schoolId ||
            schoolId === Number(SelectSchoolOption.id) ||
            !gradeLevelMasterId ||
            gradeLevelMasterId === Number(SelectGradeOption.id)
        ) {
            setClassOptions([SelectClassOption]);
            setFilterState((prev) => ({
                ...prev,
                classId: Number(SelectClassOption.id),
            }));
            return;
        }

        const newClassOptions = data
            .filter(
                (option) =>
                    option.schoolId === schoolId &&
                    (!option.gradeLevelMasterIds?.length ||
                        option.gradeLevelMasterIds.includes(gradeLevelMasterId))
            )
            .map((option) => ({ id: option.id, name: option.name }));

        if (!newClassOptions.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
        } else {
            setClassOptions([SelectClassOption, ...newClassOptions]);
            setFilterState((prev) => ({
                ...prev,
                classId: newClassOptions.some((c) => c.id === prev.classId)
                    ? prev.classId
                    : Number(SelectClassOption.id),
            }));
        }
    };

    const fetchExamOptions = async (gradeLevelMasterId: number) => {
        if (!gradeLevelMasterId || gradeLevelMasterId === Number(SelectGradeOption.id)) {
            setAllExamOptions([]);
            setExamOptions([SelectExamOption]);
            return;
        }

        setExamLoading(true);
        try {
            const res = await apiRequest<{ examOptions: ExamOption[] }>(
                'GET',
                `utility/filterData?getExam=true&gradeLevelMasterIds=${gradeLevelMasterId}&showPublishedExamsOnly=true`
            );
            const data = (res.data.examOptions ?? []).filter((exam) => exam.schoolIds?.length);

            setAllExamOptions(data);
            setExamOptions([
                SelectExamOption,
                ...data.sort((a, b) => a.name.localeCompare(b.name)),
            ]);
            setFilterState((prev) => ({ ...prev, examId: Number(SelectExamOption.id) }));
        } catch {
            setAllExamOptions([]);
            setExamOptions([SelectExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(SelectExamOption.id) }));
            setError('Failed to load exam options');
        } finally {
            setExamLoading(false);
        }
    };

    const filterExamsLocally = (newFilterState: FilterState) => {
        if (
            !allExamOptions.length ||
            !newFilterState.schoolId ||
            newFilterState.schoolId === Number(SelectSchoolOption.id) ||
            !newFilterState.gradeLevelMasterId ||
            newFilterState.gradeLevelMasterId === Number(SelectGradeOption.id)
        ) {
            setExamOptions([SelectExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(SelectExamOption.id) }));
            return;
        }

        let filtered = [...allExamOptions];
        filtered = filtered.filter((exam) => exam.schoolIds && exam.schoolIds.length > 0);

        if (newFilterState.schoolId && newFilterState.schoolId !== Number(SelectSchoolOption.id)) {
            filtered = filtered.filter((exam) => exam.schoolIds.includes(newFilterState.schoolId));
        }

        if (newFilterState.classId && newFilterState.classId !== Number(SelectClassOption.id)) {
            filtered = filtered.filter(
                (exam) =>
                    !exam.classIds ||
                    !exam.classIds.length ||
                    exam.classIds.includes(newFilterState.classId)
            );
        }

        if (!filtered.length) {
            setExamOptions([NoExamOption]);
            setFilterState((prev) => ({ ...prev, examId: Number(NoExamOption.id) }));
            return;
        }

        const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name));
        setExamOptions([SelectExamOption, ...sorted]);

        if (!sorted.some((e) => e.id === newFilterState.examId)) {
            setFilterState((prev) => ({ ...prev, examId: Number(SelectExamOption.id) }));
        }
    };

    const fetchStudentOptions = async (classId: number) => {
        try {
            const res = await apiRequest<{ studentOptions: Option[] }>(
                'GET',
                `utility/filterData?getStudentsByClass=true&classId=${classId}`
            );
            const data = res.data.studentOptions || [];
            setStudentOptions([
                AllStudentOption,
                ...data.sort((a, b) => a.name.localeCompare(b.name)),
            ]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentOption.id) }));
        } catch {
            setStudentOptions([AllStudentOption]);
            setFilterState((prev) => ({ ...prev, studentId: Number(AllStudentOption.id) }));
        }
    };

    // Initial fetch and populate dropdowns with restored filter values
    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true&getExamTypeMaster=true'
                );
                const data = res.data;

                setGradeLevelMasterOptions([SelectGradeOption, ...data.gradeLevelMasterOptions]);
                setDistrictOptions([
                    SelectDistrictOption,
                    ...data.districtOptions.sort((a, b) => a.name.localeCompare(b.name)),
                ]);

                if (
                    filterState.districtId &&
                    filterState.districtId !== Number(SelectDistrictOption.id)
                ) {
                    await fetchSchoolClassOptions(filterState.districtId);
                }

                if (
                    filterState.gradeLevelMasterId &&
                    filterState.gradeLevelMasterId !== Number(SelectGradeOption.id)
                ) {
                    await fetchExamOptions(filterState.gradeLevelMasterId);
                }
            } catch {
                setError('Failed to fetch initial filter options');
            }
            setFiltersLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            filterState &&
            filterState.schoolId &&
            filterState.schoolId !== Number(SelectSchoolOption.id) &&
            filterState.gradeLevelMasterId &&
            filterState.gradeLevelMasterId !== Number(SelectGradeOption.id) &&
            classData.length > 0
        ) {
            updateClassOptions(classData, filterState.gradeLevelMasterId, filterState.schoolId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classData, filterState.schoolId, filterState.gradeLevelMasterId]);

    useEffect(() => {
        filterExamsLocally(filterState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState.schoolId, filterState.classId, allExamOptions]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                justifyContent: 'center',
                mb: 2,
            }}
        >
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="district-select"
                    label="District *"
                    {...getLoadingProps(
                        districtOptions,
                        Number(
                            getValidSelectValue(
                                filterState.districtId,
                                districtOptions,
                                districtOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newDistrictId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            districtId: newDistrictId,
                            schoolId: Number(SelectSchoolOption.id),
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                            examId: Number(SelectExamOption.id),
                        }));
                        setSchoolOptions([SelectSchoolOption]);
                        setClassOptions([SelectClassOption]);
                        setExamOptions([SelectExamOption]);
                        if (newDistrictId !== Number(SelectDistrictOption.id)) {
                            fetchSchoolClassOptions(newDistrictId);
                        }
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />

                <SimpleSingleSelect
                    name="school-select"
                    label="School *"
                    {...getLoadingProps(
                        schoolOptions,
                        Number(
                            getValidSelectValue(
                                filterState.schoolId,
                                schoolOptions,
                                schoolOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newSchoolId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            schoolId: newSchoolId,
                            gradeLevelMasterId: Number(SelectGradeOption.id),
                            classId: Number(SelectClassOption.id),
                        };
                        setFilterState(newState);
                        updateClassOptions(classData, Number(SelectGradeOption.id), newSchoolId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.districtId === Number(SelectDistrictOption.id)
                    }
                />

                <SimpleSingleSelect
                    name="grade-select"
                    label="Grade Level *"
                    {...getLoadingProps(
                        gradeLevelMasterOptions,
                        Number(
                            getValidSelectValue(
                                filterState.gradeLevelMasterId,
                                gradeLevelMasterOptions,
                                gradeLevelMasterOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeLevelMasterId = Number(e.target.value);
                        const newState = {
                            ...filterState,
                            gradeLevelMasterId: newGradeLevelMasterId,
                        };
                        setFilterState(newState);
                        updateClassOptions(classData, newGradeLevelMasterId, filterState.schoolId);
                        fetchExamOptions(newGradeLevelMasterId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading || filterState.schoolId === Number(SelectSchoolOption.id)
                    }
                />

                <SimpleSingleSelect
                    name="class-select"
                    label="Class *"
                    {...getLoadingProps(
                        classOptions,
                        Number(
                            getValidSelectValue(
                                filterState.classId,
                                classOptions,
                                classOptions[0]?.id
                            )
                        ),
                        filtersLoading
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newClassId = Number(e.target.value);
                        const newState = { ...filterState, classId: newClassId };
                        setFilterState(newState);
                        fetchStudentOptions(newClassId);
                    }}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        filtersLoading ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id)
                    }
                />
            </FilterBarContainer>

            <FilterBarContainer>
                <SimpleSingleSelect
                    name="exam-select"
                    label="Assessment *"
                    value={Number(
                        getValidSelectValue(filterState.examId, examOptions, examOptions[0]?.id)
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, examId: Number(e.target.value) }));
                    }}
                    options={examOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                    disabled={
                        examLoading ||
                        filterState.schoolId === Number(SelectSchoolOption.id) ||
                        filterState.gradeLevelMasterId === Number(SelectGradeOption.id) ||
                        filterState.districtId === Number(SelectDistrictOption.id) ||
                        filterState.classId === Number(SelectClassOption.id)
                    }
                />

                <SimpleSingleSelect
                    name="student-select"
                    label="Student"
                    value={Number(
                        getValidSelectValue(
                            filterState.studentId,
                            studentOptions,
                            studentOptions[0]?.id
                        )
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, studentId: Number(e.target.value) }));
                    }}
                    options={studentOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />

                <SimpleButton
                    onClick={() => {
                        const missing = getMissingFields(filterState);
                        if (missing.length) {
                            setError(`Please select: ${missing.join(', ')} before filtering.`);
                            return;
                        }
                        setError('');
                        setFieldsChanged(false);
                        setFilterState((prev) => ({ ...prev, useMock: false }));
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ padding: '8px 25px' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>

            {error && <ErrorMessage error={error} />}
        </Box>
    );
};

export default AttemptDetailsFilters;
